import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Toggle from 'react-toggle';
import '../../../../styles/react-toggle.css';
import {
    useAllocateLicenseMutation,
    useBulkDeleteCandidatesMutation,
    useCandidates,
    useCandidatesInTrialPeriod,
    useCandidatesNeverConnected,
    useCandidatesWithExpiredLicense,
    useCandidatesWithoutLicenseAfterTrialPeriod,
    useCandidatesWithPendingLicense,
    useCandidatesWithValidLicense,
    useDefineAsB2BMutation,
    useDefineAsB2CMutation,
    useSendNewEmailInvitationMutation,
} from 'core/api/candidates';
import { useCandidatesSelectionStore } from 'stores/candidatesSelection';
import { useModalStore } from 'stores/modal';
import { useSessionStore } from 'stores/session';
import Title from 'components/Typography/Title';
import FloatingButton from 'components/FloatingButton';
import Button from 'components/Button';
import { getUserLabel } from 'core/useCases/session/utils';
import { ModalType } from 'core/useCases/modal/types';
import { getNumericDate } from 'utils/dateTimeFormat';
import { STable } from 'styles/components';
import { SColumn, SSpacedRowResponsive } from 'styles/layout';
import {
    SActions,
    SCard,
    SCardHeader,
    SCardHeaderLeft,
    SCardLine,
    SCardLineLeft,
    SCardLineRight,
    SNameCell,
    SSearchInput,
    SWrapper,
} from './styles';
import Candidate from 'core/models/candidate';
import { HttpError } from 'utils/http';
import AgencyAdmin from 'core/models/agencyAdmin';
import Add from 'images/icons/Add.svg?react';
import MultiActionsButton from 'components/MultiActionsButton';
import TabButtonsGroup from 'components/TabButtonsGroup';
import TabButton from 'components/TabButton';
import CandidateStatus from 'core/enums/candidateStatus';
import useResponsive from 'hooks/responsive';
import { UserRole } from 'core/enums/userRole';
import Tag from 'components/Tag';

const tabButtons: Array<{ type: CandidateStatus | null; labelKey: string }> = [
    { labelKey: 'all', type: null },
    { labelKey: 'never-connected', type: CandidateStatus.NeverConnected },
    { labelKey: 'in-trial-period', type: CandidateStatus.InTrialPeriod },
    { labelKey: 'without-license', type: CandidateStatus.WithoutLicenseAfterTrialPeriod },
    { labelKey: 'with-pending-license', type: CandidateStatus.WithPendingLicense },
    { labelKey: 'with-valid-license', type: CandidateStatus.WithValidLicense },
    { labelKey: 'with-expired-license', type: CandidateStatus.WithExpiredLicense },
];

const CandidateList: FC = () => {
    const { t } = useTranslation();
    const { data: candidatesData } = useCandidates();
    const candidates = useMemo(() => candidatesData ?? [], [candidatesData]);
    const { data: candidatesNeverConnectedData } = useCandidatesNeverConnected();
    const candidatesNeverConnected = useMemo(() => candidatesNeverConnectedData ?? [], [candidatesNeverConnectedData]);
    const { data: candidatesInTrialPeriodData } = useCandidatesInTrialPeriod();
    const candidatesInTrialPeriod = useMemo(() => candidatesInTrialPeriodData ?? [], [candidatesInTrialPeriodData]);
    const { data: candidatesWithoutLicenseAfterTrialPeriodData } = useCandidatesWithoutLicenseAfterTrialPeriod();
    const candidatesWithoutLicenseAfterTrialPeriod = useMemo(
        () => candidatesWithoutLicenseAfterTrialPeriodData ?? [],
        [candidatesWithoutLicenseAfterTrialPeriodData],
    );
    const { data: candidatesWithPendingLicenseData } = useCandidatesWithPendingLicense();
    const candidatesWithPendingLicense = useMemo(
        () => candidatesWithPendingLicenseData ?? [],
        [candidatesWithPendingLicenseData],
    );
    const { data: candidatesWithValidLicenseData } = useCandidatesWithValidLicense();
    const candidatesWithValidLicense = useMemo(
        () => candidatesWithValidLicenseData ?? [],
        [candidatesWithValidLicenseData],
    );
    const { data: candidatesWithExpiredLicenseData } = useCandidatesWithExpiredLicense();
    const candidatesWithExpiredLicense = useMemo(
        () => candidatesWithExpiredLicenseData ?? [],
        [candidatesWithExpiredLicenseData],
    );
    const user = useSessionStore((state) => state.user) as AgencyAdmin;
    const [search, setSearch] = useState<string>('');
    const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
    const [candidateStatus, setCandidateStatus] = useState<CandidateStatus | null>(null);
    const { isMobile } = useResponsive();
    const selectedCandidatesIds = useCandidatesSelectionStore((state) => state.selectedCandidatesIds);
    const toggleCandidateSelection = useCandidatesSelectionStore((state) => state.toggleCandidateSelection);
    const clearSelectedCandidates = useCandidatesSelectionStore((state) => state.clearSelectedCandidates);
    const openModal = useModalStore((state) => state.openModal);
    const { mutateAsync: allocateLicense } = useAllocateLicenseMutation();
    const { mutateAsync: defineAsB2CMutate } = useDefineAsB2CMutation();
    const { mutateAsync: defineAsB2BMutate } = useDefineAsB2BMutation();
    const { mutateAsync: sendNewEmailInvitationMutate } = useSendNewEmailInvitationMutation();
    const { mutateAsync: bulkDeleteCandidates } = useBulkDeleteCandidatesMutation();

    const isAdmin = UserRole.Admin === user.role;

    // Patrick n'a pas créé ces candidats : il doit les reconnaître au premier coup d'œil.
    const signedUpOnTheirOwnTag = (candidate: Candidate) =>
        candidate.signedUpOnTheirOwn && (
            <Tag title={t('candidate.signed-up-on-their-own-hint')} variant="subtle">
                {t('candidate.signed-up-on-their-own')}
            </Tag>
        );

    const openCandidateModal = () => {
        openModal({
            title: t('candidate.creation.label'),
            type: ModalType.Candidate,
        });
    };

    const openCandidateDeleteModal = () => {
        if (selectedCandidatesIds.length === 0) {
            toast.error(t('candidate.delete.error-no-candidate'));
            return;
        }
        openModal({
            confirmAction: () => {
                bulkDeleteCandidates(selectedCandidatesIds)
                    .then(() => {
                        clearSelectedCandidates();
                        toast.success(t('candidate.delete.success'));
                    })
                    .catch(() => toast.error(t('candidate.delete.error')));
            },
            title: t('candidate.delete-button.label'),
            type: ModalType.DeleteCandidate,
        });
    };

    // Un admin d'agence agit pour la sienne ; l'admin général, pour celle du candidat.
    const agencyIdFor = (candidate: Candidate): string | undefined => user.agency?.id || candidate.agency?.id;

    const allocateLicence = async (candidate: Candidate) => {
        const agencyId = agencyIdFor(candidate);
        if (!agencyId) {
            toast.error(t('license.allocation.error'));
            return;
        }
        try {
            await allocateLicense({ agencyId, candidateId: candidate.id });
            toast.success(t('license.allocation.success'));
        } catch (error) {
            toast.error(t('license.allocation.error'));
        }
    };

    const defineAsB2C = async (candidateId: string) => {
        try {
            await defineAsB2CMutate(candidateId);
            toast.success(t('candidate.define-as-b2c.success'));
        } catch (error) {
            toast.error(t((error as HttpError).message));
        }
    };

    const defineAsB2B = async (candidateId: string) => {
        try {
            await defineAsB2BMutate(candidateId);
            toast.success(t('candidate.define-as-b2b.success'));
        } catch (error) {
            toast.error('Error');
        }
    };

    const sendNewEmailInvitation = async (candidate: Candidate) => {
        const agencyId = agencyIdFor(candidate);
        if (!agencyId) {
            toast.error(t('candidate.send-new-email-invitation.error'));
            return;
        }
        try {
            await sendNewEmailInvitationMutate({ agencyId, candidateId: candidate.id });
            toast.success(t('candidate.send-new-email-invitation.success'));
        } catch (error) {
            toast.error(t('candidate.send-new-email-invitation.error'));
        }
    };

    const handleCheckboxChange = (id: string) => {
        toggleCandidateSelection(id);
    };

    const getSelectedCandidates = (type: CandidateStatus | null): Array<Candidate> => {
        switch (type) {
            case CandidateStatus.InTrialPeriod:
                return candidatesInTrialPeriod;
            case CandidateStatus.NeverConnected:
                return candidatesNeverConnected;
            case CandidateStatus.WithoutLicenseAfterTrialPeriod:
                return candidatesWithoutLicenseAfterTrialPeriod;
            case CandidateStatus.WithPendingLicense:
                return candidatesWithPendingLicense;
            case CandidateStatus.WithValidLicense:
                return candidatesWithValidLicense;
            case CandidateStatus.WithExpiredLicense:
                return candidatesWithExpiredLicense;
            default:
                return candidates;
        }
    };

    useEffect(() => {
        const selectedCandidates = getSelectedCandidates(candidateStatus);

        let localCandidates = [...selectedCandidates];

        if (search) {
            const computedSearch = search.toLowerCase();
            localCandidates = localCandidates.filter(
                (candidate) =>
                    getUserLabel(candidate, t).toLowerCase().includes(computedSearch) ||
                    candidate.id === computedSearch,
            );
        }

        localCandidates = localCandidates.sort(
            (candidateA, candidateB) =>
                new Date(candidateB.createdDate).getTime() - new Date(candidateA.createdDate).getTime(),
        );

        setFilteredCandidates(localCandidates);
    }, [search, candidates, candidateStatus]);

    return (
        <SWrapper>
            <SSpacedRowResponsive>
                <SColumn>
                    <Title text={t('page.candidates.title')} />
                    <span>
                        {candidates.length} {t('common.candidates')}
                    </span>
                </SColumn>
                <SSearchInput>
                    <input
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={t('candidate.search-for-candidate')}
                        type="text"
                        value={search}
                    />
                </SSearchInput>
            </SSpacedRowResponsive>
            <TabButtonsGroup>
                {tabButtons.map(({ type, labelKey }) => (
                    <TabButton
                        key={labelKey}
                        onClick={() => setCandidateStatus(type)}
                        variant={type === candidateStatus ? 'plain' : 'text'}
                    >
                        {t(`candidates.${labelKey}`)} {getSelectedCandidates(type).length}
                    </TabButton>
                ))}
            </TabButtonsGroup>
            {isMobile ? (
                filteredCandidates.map((candidate: Candidate) => {
                    const {
                        id,
                        email,
                        job,
                        mobileNumber,
                        phoneNumber,
                        license,
                        createdDate,
                        firstLoginDate,
                        lastLoginDate,
                        trialEndDate,
                        canBeReAllocatedLicense,
                        canBeSentAgainEmailInvitation,
                    } = candidate;
                    const userLabel = getUserLabel(candidate, t);
                    return (
                        <SCard key={id}>
                            <SCardHeader>
                                <SCardHeaderLeft>
                                    {userLabel} {signedUpOnTheirOwnTag(candidate)}
                                </SCardHeaderLeft>
                                {(canBeReAllocatedLicense || canBeSentAgainEmailInvitation) && (
                                    <MultiActionsButton parentButtonContent={<Add />} iconButton>
                                        {canBeReAllocatedLicense && (
                                            <Button onClick={() => allocateLicence(candidate)} variant="popinText">
                                                {t('license.allocation.label')}
                                            </Button>
                                        )}
                                        {canBeSentAgainEmailInvitation && (
                                            <Button
                                                onClick={() => sendNewEmailInvitation(candidate)}
                                                variant="popinText"
                                            >
                                                {t('candidate.send-new-email-invitation.label')}
                                            </Button>
                                        )}
                                    </MultiActionsButton>
                                )}
                            </SCardHeader>
                            <SCardLine>
                                <SCardLineLeft>{t('common.job.label')}</SCardLineLeft>
                                <SCardLineRight>{job}</SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('common.email.label')}</SCardLineLeft>
                                <SCardLineRight>{email}</SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('common.phone')}</SCardLineLeft>
                                <SCardLineRight>
                                    {mobileNumber} <br />
                                    {phoneNumber}
                                </SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>
                                    {t('candidate.created-date')}
                                    <br />
                                    {t('candidate.first-login')}
                                </SCardLineLeft>
                                <SCardLineRight>
                                    {getNumericDate(createdDate)}
                                    <br />
                                    {firstLoginDate && getNumericDate(firstLoginDate)}
                                </SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('candidate.last-login')}</SCardLineLeft>
                                <SCardLineRight>{lastLoginDate && getNumericDate(lastLoginDate)}</SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('candidate.trial-end-date')}</SCardLineLeft>
                                <SCardLineRight>{trialEndDate && getNumericDate(trialEndDate)}</SCardLineRight>
                            </SCardLine>
                            {isAdmin && (
                                <td>
                                    {candidate.isB2C && (
                                        <Toggle
                                            aria-labelledby="candidate-is-b2c"
                                            defaultChecked={candidate.isB2C}
                                            id="candidate-is-b2c"
                                            onChange={() => defineAsB2B(id)}
                                        />
                                    )}
                                    {!candidate.isB2C && (
                                        <Toggle
                                            aria-labelledby="candidate-is-b2c"
                                            defaultChecked={candidate.isB2C}
                                            id="candidate-is-b2c"
                                            onChange={() => defineAsB2C(id)}
                                        />
                                    )}
                                </td>
                            )}
                            <SCardLine>
                                <SCardLineLeft>{t('license.allocation-date')}</SCardLineLeft>
                                <SCardLineRight>
                                    {license?.invitationDate && getNumericDate(license.invitationDate)}
                                </SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>{t('license.activation-date')}</SCardLineLeft>
                                <SCardLineRight>
                                    {license?.activationDate && getNumericDate(license.activationDate)}
                                </SCardLineRight>
                            </SCardLine>
                            <SCardLine>
                                <SCardLineLeft>
                                    {t('license.activation-date')}
                                    <br />
                                    {t('license.expiration-date')}
                                </SCardLineLeft>
                                <SCardLineRight>
                                    {license?.activationDate && getNumericDate(license.activationDate)}
                                    <br />
                                    {license?.expirationDate && getNumericDate(license.expirationDate)}
                                </SCardLineRight>
                            </SCardLine>
                        </SCard>
                    );
                })
            ) : (
                <STable>
                    <thead>
                        <tr>
                            {isAdmin && <th>{t('common.label')}</th>}
                            <th>{t('common.last-name.label')}</th>
                            <th>{t('common.email.label')}</th>
                            <th>{t('common.phone')}</th>
                            <th>{t('common.origin.label')}</th>
                            {isAdmin && <th>{t('common.agency.label')}</th>}
                            <th>{t('candidate.created-date')}</th>
                            <th>{t('candidate.first-login')}</th>
                            <th>{t('candidate.last-login')}</th>
                            <th>{t('license.allocation-date')}</th>
                            <th>{t('license.activation-date')}</th>
                            <th>{t('license.expiration-date')}</th>
                            <th>{t('candidate.trial-end-date')}</th>
                            {isAdmin && <th>{t('candidate.isB2C')}</th>}
                            {isAdmin && <th>{t('candidate.hasPaid')}</th>}
                            <th aria-label={t('common.action')} />
                        </tr>
                    </thead>
                    <tbody className="table--candidates">
                        {filteredCandidates.map((candidate: Candidate) => {
                            const {
                                id,
                                email,
                                mobileNumber,
                                phoneNumber,
                                origin,
                                license,
                                createdDate,
                                firstLoginDate,
                                lastLoginDate,
                                canBeReAllocatedLicense,
                                canBeSentAgainEmailInvitation,
                                agency,
                                trialEndDate,
                            } = candidate;
                            const userLabel = getUserLabel(candidate, t);
                            return (
                                <tr key={id}>
                                    {isAdmin && (
                                        <td>
                                            <input
                                                checked={selectedCandidatesIds.includes(id)}
                                                onChange={() => handleCheckboxChange(id)}
                                                type="checkbox"
                                            />
                                        </td>
                                    )}
                                    <td>
                                        <SNameCell>
                                            {userLabel}
                                            {signedUpOnTheirOwnTag(candidate)}
                                        </SNameCell>
                                    </td>
                                    <td>{email}</td>
                                    <td>
                                        {mobileNumber} <br />
                                        {phoneNumber}
                                    </td>
                                    <td>{origin}</td>
                                    {isAdmin && <td>{agency?.name || ''}</td>}
                                    <td>{getNumericDate(createdDate)}</td>
                                    <td>{firstLoginDate && getNumericDate(firstLoginDate)}</td>
                                    <td>{lastLoginDate && getNumericDate(lastLoginDate)}</td>
                                    <td>{license?.invitationDate && getNumericDate(license.invitationDate)}</td>
                                    <td>{license?.activationDate && getNumericDate(license.activationDate)}</td>
                                    <td>{license?.expirationDate && getNumericDate(license.expirationDate)}</td>
                                    <td>{trialEndDate && getNumericDate(trialEndDate)}</td>
                                    {isAdmin && (
                                        <td>
                                            {candidate.isB2C && (
                                                <Toggle
                                                    aria-labelledby="candidate-is-b2c"
                                                    defaultChecked={candidate.isB2C}
                                                    id="candidate-is-b2c"
                                                    onChange={() => defineAsB2B(id)}
                                                />
                                            )}
                                            {!candidate.isB2C && (
                                                <Toggle
                                                    aria-labelledby="candidate-is-b2c"
                                                    defaultChecked={candidate.isB2C}
                                                    id="candidate-is-b2c"
                                                    onChange={() => defineAsB2C(id)}
                                                />
                                            )}
                                        </td>
                                    )}
                                    {isAdmin && (
                                        <td>
                                            {candidate.isB2C &&
                                                (candidate.hasPaid ? (
                                                    <Tag color="#19AB27">{t('candidate.paid')}</Tag>
                                                ) : (
                                                    <Tag color="#E94A41">{t('candidate.not-paid')}</Tag>
                                                ))}
                                        </td>
                                    )}
                                    <td>
                                        <SActions className="showOnRowHover">
                                            {(canBeReAllocatedLicense || canBeSentAgainEmailInvitation) && (
                                                <MultiActionsButton parentButtonContent={<Add />} iconButton>
                                                    {canBeReAllocatedLicense && (
                                                        <Button
                                                            onClick={() => allocateLicence(candidate)}
                                                            variant="popinText"
                                                        >
                                                            {t('license.allocation.label')}
                                                        </Button>
                                                    )}
                                                    {canBeSentAgainEmailInvitation && (
                                                        <Button
                                                            onClick={() => sendNewEmailInvitation(candidate)}
                                                            variant="popinText"
                                                        >
                                                            {t('candidate.send-new-email-invitation.label')}
                                                        </Button>
                                                    )}
                                                </MultiActionsButton>
                                            )}
                                        </SActions>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </STable>
            )}

            <FloatingButton>
                <Button onClick={openCandidateModal} large withShadow>
                    {t('candidate.creation.label')}
                </Button>

                {isAdmin && (
                    <Button onClick={openCandidateDeleteModal} variant="outlinedRed" withShadow>
                        {t('candidate.delete-button.label')}
                    </Button>
                )}
            </FloatingButton>
        </SWrapper>
    );
};

export default CandidateList;
